import type { Money } from '@dastarhan/contracts';

/**
 * Доставка: зоны, стоимость, движение заказа.
 *
 * Зона у нас — кольцо вокруг точки, а не полигон на карте. Это сознательный
 * выбор: полигоны требуют картографического редактора и постоянной поддержки,
 * а кольцо владелец задаёт за минуту и понимает без обучения. Когда кольца
 * перестанет хватать, полигон можно добавить поверх — модель это допускает.
 */

export type DeliveryZone = {
  name: string;
  /** внешняя граница кольца в километрах от точки */
  radiusKm: number;
  /** стоимость доставки в этой зоне */
  price: Money;
  /** бесплатно от этой суммы заказа; null — платно всегда */
  freeFrom: Money | null;
  /** обещанное время в минутах */
  etaMin: number;
  /** минимальная сумма заказа для этой зоны */
  minSum: Money;
};

/** Зона по расстоянию: берём первое подходящее кольцо. */
export function zoneFor(distanceKm: number, zones: DeliveryZone[]): DeliveryZone | null {
  if (distanceKm < 0) return null;
  const sorted = [...zones].sort((a, b) => a.radiusKm - b.radiusKm);
  return sorted.find((z) => distanceKm <= z.radiusKm) ?? null;
}

/** Стоимость доставки с учётом порога бесплатной. */
export function deliveryPrice(zone: DeliveryZone | null, orderSum: Money): Money {
  if (!zone) return 0;
  if (zone.freeFrom !== null && orderSum >= zone.freeFrom) return 0;
  return zone.price;
}

/**
 * Можно ли вообще принять заказ: вне зон — нет, ниже минимальной суммы — нет.
 * Отказать сразу честнее, чем принять и не довезти.
 */
export function canAccept(
  distanceKm: number,
  orderSum: Money,
  zones: DeliveryZone[],
): { ok: true; zone: DeliveryZone } | { ok: false; reason: string } {
  const zone = zoneFor(distanceKm, zones);
  if (!zone) {
    return { ok: false, reason: 'Адрес вне зоны доставки' };
  }
  if (orderSum < zone.minSum) {
    const left = zone.minSum - orderSum;
    return {
      ok: false,
      reason: `До минимальной суммы не хватает ${Math.round(left / 100).toLocaleString('ru-RU')} ₸`,
    };
  }
  return { ok: true, zone };
}

export type DeliveryStatus = 'NEW' | 'ASSIGNED' | 'ON_WAY' | 'DELIVERED' | 'CANCELLED';

export const DELIVERY_STATUS_RU: Record<DeliveryStatus, string> = {
  NEW: 'Новый',
  ASSIGNED: 'Назначен',
  ON_WAY: 'В пути',
  DELIVERED: 'Доставлен',
  CANCELLED: 'Отменён',
};

/** Куда можно перейти из статуса. Пустой список — конец пути. */
const FLOW: Record<DeliveryStatus, DeliveryStatus[]> = {
  NEW: ['ASSIGNED', 'CANCELLED'],
  ASSIGNED: ['ON_WAY', 'NEW', 'CANCELLED'],
  ON_WAY: ['DELIVERED', 'CANCELLED'],
  DELIVERED: [],
  CANCELLED: [],
};

export function canMoveTo(from: DeliveryStatus, to: DeliveryStatus): boolean {
  return FLOW[from].includes(to);
}

export function nextStatuses(from: DeliveryStatus): DeliveryStatus[] {
  return FLOW[from];
}

/** Назначение курьера обязательно до выезда: без него «в пути» не бывает. */
export function requiresCourier(status: DeliveryStatus): boolean {
  return status === 'ASSIGNED' || status === 'ON_WAY';
}

/**
 * Опаздывает ли заказ. Считаем от создания и сравниваем с обещанным
 * временем зоны: гостю обещали, и это единственный честный отсчёт.
 */
export function isLate(createdAt: Date, etaMin: number, now: Date): boolean {
  return now.getTime() - createdAt.getTime() > etaMin * 60_000;
}

/** Сколько минут в пути или сколько прошло с создания. */
export function minutesSince(from: Date, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - from.getTime()) / 60_000));
}

/**
 * Расстояние по прямой между точками (формула гаверсинуса).
 * Для колец этого достаточно: разница с дорожным расстоянием
 * укладывается в запас, который владелец и так закладывает в зону.
 */
export function distanceKm(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(h)) * 100) / 100;
}
